import { Button, Link } from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { Translate } from "../i18n/Translate";

import { getAppConfig } from "../config";
import { useAtomValue } from "jotai";
import { useChatDrawerButtonUtils } from "./chat/useChatDrawerButtonUtils";
import {
  visitorDrawerStateAtom,
  providerIsOnlineAtom,
  VisitorDrawerState,
} from "./state";
import { useContactUs } from "../useContactUs";
type ProviderNotHereDescriptionProps = {
  providerIsOnline: boolean | null;
  clientHasAppointment: boolean;
  appointmentHasProviderNoShowReport: boolean;
  isPastNoShowThreshold: boolean;
  clientJoinedAfterWindowToReport: boolean;
  recordProviderNoShowAndRedirect: () => void;
  onOpenChatDrawer: () => void;
  onClose: () => void;
};
export function ProviderNotHereDescription({
  providerIsOnline,
  clientHasAppointment,
  appointmentHasProviderNoShowReport,
  isPastNoShowThreshold,
  clientJoinedAfterWindowToReport,
  recordProviderNoShowAndRedirect,
  onOpenChatDrawer,
  onClose,
}: ProviderNotHereDescriptionProps) {
  const t = useTranslate();
  const marketplaceOrigin = getAppConfig().marketplaceOrigin;
  const { pageUrl } = useContactUs();

  const supportText = (
    <Translate
      i18nKey="telehealth-core:providerNotHere.thereIsNoSessionScheduledForThisTime"
      components={{
        1: (
          <Link
            href={pageUrl}
            size="md"
            className="text-neutral-800 hover:text-neutral-900"
          />
        ),
      }}
    />
  );

  if (providerIsOnline) {
    return (
      <Translate
        i18nKey="telehealth-core:providerNotHere.yourProviderAppearsToBeOnlineButHasntAdmittedYou"
        components={{
          1: (
            <Button
              use="link"
              onClick={onOpenChatDrawer}
              className="text-neutral-800 hover:text-neutral-900"
            />
          ),
        }}
      />
    );
  }

  if (!clientHasAppointment) {
    return supportText;
  }
  if (appointmentHasProviderNoShowReport) {
    return (
      <Translate
        i18nKey="telehealth-core:providerNotHere.weReceivedAConfirmationThatYourProviderDidntShowUp"
        components={{
          1: (
            <Link
              external
              className="text-neutral-800 font-medium hover:text-neutral-900"
              href={`${marketplaceOrigin}/filters?utm_source=telehealth-booking`}
            />
          ),
        }}
      />
    );
  }

  if (clientJoinedAfterWindowToReport) {
    return (
      <>
        {t(
          "telehealth-core:providerNotHere.theGracePeriodForTheSessionHasPassed",
        )}
      </>
    );
  }

  return (
    <>
      {t("telehealth-core:providerNotHere.yourProviderHasA10MinuteGracePeriod")}
      <Button
        disabled={!isPastNoShowThreshold}
        className="w-full mt-3"
        aria-live="polite"
        onClick={() => {
          recordProviderNoShowAndRedirect();
          onClose();
        }}
      >
        {isPastNoShowThreshold && (
          <span className="sr-only">
            {t("telehealth-core:providerNotHere.buttonEnabled")}
          </span>
        )}
        {t("telehealth-core:providerNotHere.myProviderIsNotHere")}
      </Button>
    </>
  );
}

export function ProviderNotHereDescriptionWrapper(
  props: Omit<
    ProviderNotHereDescriptionProps,
    "providerIsOnline" | "onOpenChatDrawer"
  >,
) {
  const { toggleChatDrawer } = useChatDrawerButtonUtils();
  const drawerState = useAtomValue(visitorDrawerStateAtom);
  const providerIsOnline = useAtomValue(providerIsOnlineAtom);

  return (
    <ProviderNotHereDescription
      providerIsOnline={providerIsOnline}
      onOpenChatDrawer={() => {
        if (drawerState !== VisitorDrawerState.CHAT) {
          toggleChatDrawer();
        }
        props.onClose();
      }}
      {...props}
    />
  );
}
